import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const SAMPLE_CSV = `category,amount,vendor,description,project,date
Materials,125000,Asian Paints,Interior emulsion 20L x 8,Malhotra Duplex Interiors,2026-02-10
Labour,68000,Ravi Contractor,Masonry team weekly wages,Malhotra Duplex Interiors,2026-02-12
Equipment,42000,BuildRent Co,Concrete mixer 3 day rental,Aakash Tower Block A,2026-02-14
Transport,8500,Blue Dart Logistics,Marble slab delivery,Malhotra Duplex Interiors,2026-02-15
Utilities,15400,BESCOM,Site electricity Feb bill,Aakash Tower Block A,2026-02-16
Design,55000,Studio Kalakriti,3D visualisation renders,Meridian Heritage Hotel,2026-02-18
Marketing,32000,Meta Ads,Instagram lead campaign,,2026-02-20
Materials,89500,Kajaria Ceramics,Vitrified tiles 800x1600,Menon Kochi Home,2026-02-22
Other,12000,Zomato Corporate,Site team lunches,Aakash Tower Block A,2026-02-24`;

function parseCSV(text) {
  const lines = text.replace(/\r/g,"").split("\n").filter(l => l.trim());
  if (!lines.length) return [];
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i+1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = !inQ;
      else if (c === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur); return out;
  };
  const header = parseLine(lines[0]).map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const cells = parseLine(line);
    const obj = {};
    header.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
    return obj;
  }).filter(r => r.amount || r.category);
}

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ category: "Materials", amount: 0, vendor: "", description: "", project_id: "", date: new Date().toISOString().slice(0,10) });
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/expenses").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const add = async () => {
    if (!form.amount) return toast.error("Amount required");
    await api.post("/expenses", form); toast.success("Expense added"); load();
    setForm({ category: "Materials", amount: 0, vendor: "", description: "", project_id: "", date: new Date().toISOString().slice(0,10) });
  };
  const remove = async (id) => { await api.delete(`/expenses/${id}`); load(); };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const text = await file.text();
      const items = parseCSV(text);
      if (!items.length) { toast.error("No valid rows found"); setImporting(false); return; }
      const { data } = await api.post("/expenses/bulk", { items });
      setImportResult(data);
      if (data.ok) toast.success(`Imported ${data.ok} expenses`);
      if (data.fail) toast.error(`${data.fail} rows failed`);
      load();
    } catch (err) {
      toast.error("Import failed: " + err.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sample_expenses.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const total = rows.reduce((s, e) => s + Number(e.amount || 0), 0);
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Finance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Expenses</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl px-4 py-3 bg-card border border-border/60">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total spend</div>
            <div className="font-display text-xl mt-1">{fmt(total)}</div>
          </div>
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-expenses-btn">
                <Upload className="w-4 h-4 mr-1.5" />Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Import Expenses from CSV</DialogTitle></DialogHeader>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">CSV Format</div>
                <div className="text-xs font-mono">category,amount,vendor,description,project,date</div>
                <ul className="text-xs text-muted-foreground space-y-1 pl-2">
                  <li>• <strong>amount</strong> is required and must be &gt; 0 (numeric ₹)</li>
                  <li>• <strong>category</strong>: Materials, Labour, Equipment, Transport, Utilities, Design, Marketing, Other</li>
                  <li>• <strong>project</strong>: exact project name (leave blank for general/overhead)</li>
                  <li>• <strong>date</strong>: YYYY-MM-DD (defaults to today)</li>
                  <li>• Great for bulk-loading site logs, vendor invoices, petty cash books</li>
                </ul>
                <Button variant="ghost" size="sm" onClick={downloadSample} className="text-primary p-0 h-auto"><Download className="w-3.5 h-3.5 mr-1" /> Download sample CSV (9 rows)</Button>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" data-testid="csv-file-input" />
              {importing && <div className="text-sm text-muted-foreground flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 animate-pulse" /> Importing…</div>}
              {importResult && (
                <div className={`p-3 rounded-lg ${importResult.ok > 0 ? "bg-accent/20 border border-accent/40" : "bg-destructive/10 border border-destructive/30"}`}>
                  <div className="text-sm font-medium">✓ {importResult.ok} imported · ⚠ {importResult.fail} failed</div>
                  {importResult.errors?.length > 0 && (
                    <ul className="text-xs text-destructive mt-2 space-y-0.5">{importResult.errors.map((er, i) => <li key={i}>• {er}</li>)}</ul>
                  )}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground"><span className="opacity-70">ℹ</span> To import from Excel: File → Save As → CSV (Comma delimited)</div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-2xl p-4 bg-card border border-border/60 grid grid-cols-1 md:grid-cols-6 gap-2">
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{["Materials","Labour","Equipment","Transport","Utilities","Design","Marketing","Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} />
        <Input placeholder="Vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
        <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
          <SelectTrigger><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <Button onClick={add} className="bg-primary text-primary-foreground" data-testid="log-expense-btn"><Plus className="w-4 h-4 mr-1" /> Log</Button>
      </div>

      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr><th className="text-left py-3 px-4">Date</th><th className="text-left">Category</th><th className="text-left">Vendor</th><th className="text-left">Project</th><th className="text-right">Amount</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                <td className="py-3 px-4">{r.date}</td>
                <td><span className="text-xs px-2 py-1 rounded bg-muted">{r.category}</span></td>
                <td>{r.vendor}</td>
                <td className="text-muted-foreground">{projectName(r.project_id)}</td>
                <td className="text-right font-medium">{fmt(r.amount)}</td>
                <td className="pr-4 text-right"><button onClick={() => remove(r.id)} className="p-1.5 text-destructive hover:bg-muted/60 rounded"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
